import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Badge from 'react-bootstrap/Badge';

import { includes } from 'lodash';
import {
  CLOUDCMA_PAGE_CLICKED,
  CONDO_MANAGEMENT_PAGE_CLICKED,
  CREAWEBFORMS_PAGE_CLICKED,
  CREADDF_PAGE_CLICKED,
  HOME_BUTTON_CLICKED,
  REMAPLIST_PAGE_CLICKED,
  SEARCH_AGENTS_PAGE_CLICKED,
  SEARCH_LISTINGS_PAGE_CLICKED,
  MATRIX_PAGE_CLICKED,
  OFFICE_PAGE_CLICKED,
  PROSPECTS_PAGE_CLICKED,
  SPACELIST_PAGE_CLICKED,
  SPIN_PAGE_CLICKED,
} from '@redux/constants/ActionTypes';

import { loadFormDefinition } from '@redux/modules/formGeneration';
import { clearPhotoCollection } from '@redux/modules/photo';
import { clearRepeatableSectionCount } from '@redux/modules/listing';
import { clearAgent } from '@redux/modules/agent';
import useObjectState from '@hooks/useObjectState';
import useAcl from '@hooks/useAcl';

import P9CoverLarge from '@images/covers/cover-7-lg.png';
import P9Logo from '@images/logos/P9LogoWhite.png';
import WhitePillars from '@images/logos/pillars-white.png';
import SupportFooter from './SupportFooter';

const appSettings = require('../../../appsettings.json');

const menuInitialState = {
  agentNav: false,
  listingNav: false,
  createListingNav: false,
  officesNav: false,
  creaNav: false,
  resourcesNav: false,
};

const Sidebar = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [{
    agentNav,
    listingNav,
    createListingNav,
    officesNav,
    creaNav,
  }, setMenuState] = useObjectState({
    agentNav: false,
    listingNav: false,
    createListingNav: false,
    officesNav: false,
    creaNav: false,
  });

  const { mainActions } = useAcl();

  const updateAgentNav = e => {
    e.stopPropagation();
    setMenuState({ ...menuInitialState, agentNav: !agentNav });
  };

  // toggles listings menu
  const updateListingNav = e => {
    e.stopPropagation();
    setMenuState({ ...menuInitialState, listingNav: !listingNav });
  };

  // toggles create listings submenu
  const updateCreateNav = e => {
    e.stopPropagation(); // to prevent submenu click from closing the menu
    setMenuState({ createListingNav: !createListingNav });
  };

  // toggles office menu
  const updateOfficesNav = e => {
    e.stopPropagation();
    setMenuState({ ...menuInitialState, officesNav: !officesNav });
  };

  // toggles crea menu
  const updateCreaNav = e => {
    e.stopPropagation();
    setMenuState({ ...menuInitialState, creaNav: !creaNav });
  };

  // loads form definitions based on the data attribute of the <li> that was clicked
  const handleClicked = e => {
    e.stopPropagation();
    const clickedItem = e?.currentTarget?.dataset?.id;
    if (clickedItem) {
      const [operationType, definitionType, listingType] = clickedItem.split('-');
      /*
        operationType: CRUD operation
        definitionType: Agent, Listing, Offices, etc
        listingType: rest, rnt, farm, etc. Only available when the definitionType is listing
      */
      if (definitionType === 'listings') {
        dispatch(loadFormDefinition(operationType, definitionType, listingType, null, 'listing'));
      } else if (definitionType !== 'listings' && clickedItem === 'create-office') {
        dispatch(loadFormDefinition(operationType, definitionType, listingType, null, 'officeACL'));
      } else if (definitionType !== 'listings' && clickedItem === 'create-agent') {
        dispatch(loadFormDefinition(operationType, definitionType, listingType, null, 'agentACL'));
      } else {
        dispatch(loadFormDefinition(operationType, definitionType, listingType));
      }
      dispatch(clearPhotoCollection()); // resets photos
      dispatch(clearRepeatableSectionCount()); // resets Section Nav badge counters.
      dispatch(clearAgent());
    }
  };

  const handleHomeClicked = () => { dispatch({ type: HOME_BUTTON_CLICKED }); };

  const getSidebarLinkClass = path => (
    location.pathname === `/${path}` ? 'nav-menu-link active' : 'nav-menu-link'
  );

  const getSidebarCreateLinkClass = entity => (
    location.pathname === `/${entity}/create` ? 'nav-sub-header nav-menu-link active' : 'nav-sub-header nav-menu-link'
  );

  const getSidebarSearchLinkClass = entity => (
    location.pathname === `/${entity}/search` ? 'nav-sub-header nav-menu-link active' : 'nav-sub-header nav-menu-link'
  );

  // sets class to active if current url has a listing type that matches the <li> data-id type
  const getSidebarCreateListingLinkClass = path => (
    location.pathname === `/listings/create/${path}` ? 'nav-menu-link active' : 'nav-menu-link'
  );

  const handleSearchListingsClicked = e => {
    e.stopPropagation(); // to prevent submenu click from closing the menu
    dispatch({ type: SEARCH_LISTINGS_PAGE_CLICKED });
  };

  const handleSearchAgentClicked = e => {
    e.stopPropagation();
    dispatch({ type: SEARCH_AGENTS_PAGE_CLICKED });
  };

  const handleCondoManagementClicked = e => {
    e.stopPropagation();
    dispatch({ type: CONDO_MANAGEMENT_PAGE_CLICKED });
  };

  const handleOfficePageClicked = e => {
    e.stopPropagation();
    dispatch({ type: OFFICE_PAGE_CLICKED });
  };

  const handleMatrixClicked = e => {
    e.stopPropagation();
    dispatch({ type: MATRIX_PAGE_CLICKED });
  };

  const handleREMapListClicked = e => {
    e.stopPropagation();
    dispatch({ type: REMAPLIST_PAGE_CLICKED });
  };

  const handleCREAWEBFormsClicked = e => {
    e.stopPropagation();
    dispatch({ type: CREAWEBFORMS_PAGE_CLICKED });
  };

  const handleCREADDFClicked = e => {
    e.stopPropagation();
    dispatch({ type: CREADDF_PAGE_CLICKED });
  };

  const handleProspectsClicked = e => {
    e.stopPropagation();
    dispatch({ type: PROSPECTS_PAGE_CLICKED });
  };

  const handleSpinClicked = e => {
    e.stopPropagation();
    dispatch({ type: SPIN_PAGE_CLICKED });
  };

  const handleSpaceListClicked = e => {
    e.stopPropagation();
    dispatch({ type: SPACELIST_PAGE_CLICKED });
  };

  const handleCloudCMAClicked = e => {
    e.stopPropagation();
    dispatch({ type: CLOUDCMA_PAGE_CLICKED });
  };

  return (
    <aside className="page-sidebar">
      <div
        className="page-logo press-scale-down"
        onClick={includes(getSidebarLinkClass(''), 'active') ? null : handleHomeClicked}
      >
        <a className="page-logo-link d-flex align-items-center position-relative">
          <img
            src={WhitePillars}
            alt="P9 Logo Pillars"
          />
          <span className="page-logo-text ml-2">
            {appSettings.SmartSettings.AppName}
          </span>
          <span className="position-absolute text-white opacity-50 small pos-top pos-right mr-2 mt-n2">
            {appSettings.SmartSettings.AppFlavorSubscript}
          </span>
        </a>
      </div>
      <div className="info-card">
        <img src={P9Logo} alt="Pillar 9™ Logo" className="logo" />
        <div className="info-card-text" />
        <img className="cover" alt="cover" src={P9CoverLarge} />
      </div>
      <nav
        className="primary-nav js-list-filter"
        id="js-primary-nav"
        role="navigation"
      >
        <ul id="js-nav-menu" className="nav-menu js-nav-built ">
          <li className={getSidebarLinkClass('')}>
            <a
              className="sidebar-link waves-effect waves-themed"
              onClick={includes(getSidebarLinkClass(''), 'active') ? null : handleHomeClicked}
            >
              <i className="fal fa-window" />
              <span className="nav-link-text">Dashboard</span>
            </a>
          </li>
          <li className={getSidebarLinkClass('matrix')}>
            <a
              className="sidebar-link waves-effect waves-themed"
              onClick={handleMatrixClicked}
            >
              <i className="fal fa-laptop-house" />
              <span className="nav-link-text">Matrix™</span>
            </a>
          </li>
          {mainActions.Listings && (
            <>
              <li
                aria-hidden="false"
                className={listingNav ? 'active open' : ''}
                onClick={e => updateListingNav(e)}
              >
                <a className="sidebar-link waves-effect waves-themed">
                  <i className="fal fa-ballot" />
                  <span className="nav-link-text">Listings</span>
                  <b className="collapse-sign">
                    <em className={`fal fa-angle-${listingNav ? 'up' : 'down'}`} />
                  </b>
                </a>
                <ul className={listingNav ? 'd-block' : 'd-none'}>
                  {mainActions.ListingsCreate && (
                    <li
                      aria-hidden="false"
                      className={createListingNav ? 'active open nav-sub-header' : 'nav-sub-header'}
                      onClick={e => updateCreateNav(e)}
                    >
                      <a className="sidebar-link waves-effect waves-themed">
                        <span className="nav-link-text">Create Listing</span>
                        <b className="collapse-sign">
                          <em className={`fal fa-angle-${createListingNav ? 'up' : 'down'}`} />
                        </b>
                      </a>
                      <ul
                        className={createListingNav ? 'd-block' : 'd-none'}
                        onClick={e => e.stopPropagation()}
                      >
                        <li className={getSidebarCreateListingLinkClass('res')}>
                          <a
                            className="sidebar-link waves-effect waves-themed"
                            data-id="create-listings-res"
                            onClick={handleClicked}
                          >
                            <i className="fas fa-home-lg" />
                            <span className="nav-link-text">Residential</span>
                          </a>
                        </li>
                        <li className={getSidebarCreateListingLinkClass('mult')}>
                          <a
                            className="sidebar-link waves-effect waves-themed"
                            data-id="create-listings-mult"
                            onClick={handleClicked}
                          >
                            <i className="fas fa-building" />
                            <span className="nav-link-text">Multi-family</span>
                          </a>
                        </li>
                        <li className={getSidebarCreateListingLinkClass('rnt')}>
                          <a
                            className="sidebar-link waves-effect waves-themed"
                            data-id="create-listings-rnt"
                            onClick={handleClicked}
                          >
                            <i className="fas fa-store-alt" />
                            <span className="nav-link-text">Rental</span>
                          </a>
                        </li>
                        <li className={getSidebarCreateListingLinkClass('mob')}>
                          <a
                            className="sidebar-link waves-effect waves-themed"
                            data-id="create-listings-mob"
                            onClick={handleClicked}
                          >
                            <i className="fas fa-caravan" />
                            <span className="nav-link-text">Mobile</span>
                          </a>
                        </li>
                        <li className={getSidebarCreateListingLinkClass('land')}>
                          <a
                            className="sidebar-link waves-effect waves-themed"
                            data-id="create-listings-land"
                            onClick={handleClicked}
                          >
                            <i className="fas fa-mountains" />
                            <span className="nav-link-text">Land</span>
                          </a>
                        </li>
                        <li className={getSidebarCreateListingLinkClass('com')}>
                          <a
                            className="sidebar-link waves-effect waves-themed"
                            data-id="create-listings-com"
                            onClick={handleClicked}
                          >
                            <i className="fas fa-city" />
                            <span className="nav-link-text">Commercial</span>
                          </a>
                        </li>
                        <li className={getSidebarCreateListingLinkClass('farm')}>
                          <a
                            className="sidebar-link waves-effect waves-themed"
                            data-id="create-listings-farm"
                            onClick={handleClicked}
                          >
                            <i className="fas fa-farm" />
                            <span className="nav-link-text">Agribusiness</span>
                          </a>
                        </li>
                      </ul>
                    </li>
                  )}
                  {mainActions.ListingsSearch && (
                    <li className={getSidebarSearchLinkClass('listings')}>
                      <a
                        className="sidebar-link waves-effect waves-themed"
                        onClick={handleSearchListingsClicked}
                      >
                        <span className="nav-link-text">Search Listing</span>
                      </a>
                    </li>
                  )}
                </ul>
              </li>
            </>
          )}
          {mainActions.Agents && (
            <li
              aria-hidden="false"
              className={agentNav ? 'active open' : ''}
              onClick={e => updateAgentNav(e)}
            >
              <a className="sidebar-link waves-effect waves-themed">
                <i className="fal fa-users" />
                <span className="nav-link-text">Agents</span>
                <b className="collapse-sign">
                  <em className={`fal fa-angle-${agentNav ? 'up' : 'down'}`} />
                </b>
              </a>
              <ul className={agentNav ? 'd-block' : 'd-none'}>
                {mainActions.AgentsCreate && (
                  <li className={getSidebarCreateLinkClass('agent')}>
                    <a
                      className="sidebar-link waves-effect waves-themed"
                      data-id="create-agent"
                      onClick={handleClicked}
                    >
                      <span className="nav-link-text">Create Agent</span>
                    </a>
                  </li>
                )}
                {mainActions.AgentsSearch && (
                  <li className={getSidebarSearchLinkClass('agent')}>
                    <a
                      className="sidebar-link waves-effect waves-themed"
                      onClick={handleSearchAgentClicked}
                    >
                      <span className="nav-link-text">Search Agents</span>
                    </a>
                  </li>
                )}
              </ul>
            </li>
          )}
          {mainActions.Offices && (
            <li
              aria-hidden="false"
              className={officesNav ? 'active open' : ''}
              onClick={updateOfficesNav}
            >
              <a className="sidebar-link waves-effect waves-themed">
                <i className="fal fa-briefcase" />
                <span className="nav-link-text">Offices</span>
                <b className="collapse-sign">
                  <em
                    className={`fal fa-angle-${officesNav ? 'up' : 'down'}`}
                  />
                </b>
              </a>
              <ul className={officesNav ? 'd-block' : 'd-none'}>
                {mainActions.OfficesCreate && (
                  <li className={getSidebarCreateLinkClass('office')}>
                    <a
                      className="sidebar-link waves-effect waves-themed"
                      data-id="create-office"
                      onClick={handleClicked}
                    >
                      <span className="nav-link-text">Create Office</span>
                    </a>
                  </li>
                )}
                {mainActions.OfficesSearch && (
                  <li className={getSidebarSearchLinkClass('office')}>
                    <a
                      className="sidebar-link waves-effect waves-themed"
                      onClick={handleOfficePageClicked}
                    >
                      <span className="nav-link-text">Search Offices</span>
                    </a>
                  </li>
                )}
              </ul>
            </li>
          )}
          {mainActions.CondoNameManagement && (
            <li className={getSidebarLinkClass('condo')}>
              <a
                className="sidebar-link waves-effect waves-themed"
                onClick={handleCondoManagementClicked}
              >
                <i className="fal fa-building" />
                <span className="nav-link-text">Condo Management</span>
              </a>
            </li>
          )}
          <li className={getSidebarLinkClass('remaplist')}>
            <a
              className="sidebar-link waves-effect waves-themed"
              onClick={handleREMapListClicked}
            >
              <i className="fal fa-map" />
              <span className="nav-link-text">REMapList™</span>
            </a>
          </li>
          <li
            aria-hidden="false"
            className={creaNav ? 'active open' : ''}
            onClick={updateCreaNav}
          >
            <a className="sidebar-link waves-effect waves-themed">
              <i className="fad fa-chevron-right" />
              <span className="nav-link-text">CREA</span>
              <b className="collapse-sign">
                <em
                  className={`fal fa-angle-${creaNav ? 'up' : 'down'}`}
                />
              </b>
            </a>
            <ul className={creaNav ? 'd-block' : 'd-none'}>
              <li className={location.pathname === '/crea/webforms' ? 'nav-sub-header nav-menu-link active' : 'nav-sub-header nav-menu-link'}>
                <a
                  className="sidebar-link waves-effect waves-themed"
                  onClick={handleCREAWEBFormsClicked}
                >
                  <span className="nav-link-text">CREA WEBForms®</span>
                </a>
              </li>
              <li className={location.pathname === '/crea/ddf' ? 'nav-sub-header nav-menu-link active' : 'nav-sub-header nav-menu-link'}>
                <a
                  className="sidebar-link waves-effect waves-themed"
                  onClick={handleCREADDFClicked}
                >
                  <span className="nav-link-text">CREA DDF®</span>
                </a>
              </li>
            </ul>
          </li>
          <li className={getSidebarLinkClass('prospects')}>
            <a
              className="sidebar-link waves-effect waves-themed"
              onClick={handleProspectsClicked}
            >
              <i className="fal fa-address-card" />
              <span className="nav-link-text">Prospects</span>
            </a>
          </li>
          <li className={getSidebarLinkClass('spin')}>
            <a
              className="sidebar-link waves-effect waves-themed"
              onClick={handleSpinClicked}
            >
              <i className="fal fa-search-location" />
              <span className="nav-link-text">Spin2</span>
            </a>
          </li>
          <li className={getSidebarLinkClass('spacelist')}>
            <a
              className="sidebar-link waves-effect waves-themed"
              onClick={handleSpaceListClicked}
            >
              <i className="fal fa-city" />
              <span className="nav-link-text">Spacelist</span>
            </a>
          </li>
          <li className={getSidebarLinkClass('cloudcma')}>
            <a
              className="sidebar-link waves-effect waves-themed"
              onClick={handleCloudCMAClicked}
            >
              <i className="fal fa-cloud" />
              <span className="nav-link-text">
                Cloud CMA
                <sup>
                  <Badge variant="success" className="ml-2 mb-1">NEW</Badge>
                </sup>
              </span>
            </a>
          </li>
        </ul>
      </nav>
      <SupportFooter />
    </aside>
  );
};

export default Sidebar;
